// The promise the render route makes the driver: every injected chart is answered exactly once on
// the aytm:chartReady channel. This hook is the part of that promise the chart itself cannot keep —
// a payload the shell could not resolve, a crash outside the React tree, and a chart that simply
// never reports.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/driver-call-examples.md §0

import { useEffect } from 'react'

import {
  emitChartReady,
  readExportDebug,
  recordExportDebug
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import type { ExportPayload } from './types'

// Under the driver's own 30s timeout: it gets a cheap error event (skip the chart, keep the warm
// page) instead of an expensive browser kill and queue restart.
const WATCHDOG_MS = 25000

// Headless Chromium raises these on its own while the chart is perfectly fine. Answering one would
// suppress the real chartReady that follows, so they only enrich the debug snapshot.
const ALLOWED_ERRORS = [/ResizeObserver loop/i, /^Script error\.?$/i]

const isAllowedError = (message: string) => ALLOWED_ERRORS.some(pattern => pattern.test(message))

const messageOf = (reason: any, fallback: string) => String(reason?.message || reason || fallback)

/**
 * @param payload - the chart currently injected, or null before the first injection
 * @param error - a failure the shell resolved before mounting anything (unknown type, bad payload)
 */
export function useChartReadyGuard(payload: ExportPayload | null, error?: string): void {
  useEffect(() => {
    if (!payload) return

    const { requestId, params } = payload

    recordExportDebug({ label: 'shell', requestId, chartType: params.chartType })

    if (error) {
      emitChartReady({ requestId, error })
      return
    }

    const settle = (kind: string, message: string) => {
      if (isAllowedError(message)) recordExportDebug({ label: `allowed ${kind}`, requestId, message })
      else emitChartReady({ requestId, error: `${kind}: ${message}` })
    }

    // Async crashes outside the React tree — the error boundary never sees these.
    const onError = (event: ErrorEvent) => settle('uncaught error', event.message || messageOf(event.error, 'unknown'))
    const onRejection = (event: PromiseRejectionEvent) =>
      settle('unhandled rejection', messageOf(event.reason, 'unknown'))

    const watchdogMs = Number(params.watchdogMs) || WATCHDOG_MS
    const watchdog = window.setTimeout(() => {
      emitChartReady({
        requestId,
        error: `watchdog: no chartReady within ${watchdogMs}ms; state=${JSON.stringify(readExportDebug())}`
      })
    }, watchdogMs)

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)

    return () => {
      window.clearTimeout(watchdog)
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [payload, error])
}
